import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['image']

  open(event) {
    const src = event.currentTarget.querySelector('img')?.src || event.currentTarget.src
    const alt = event.currentTarget.querySelector('img')?.alt || event.currentTarget.alt || ''

    this.scale = 1
    this.originX = 0
    this.originY = 0
    this.isDragging = false

    const overlay = document.createElement('div')
    overlay.id = 'lightbox-overlay'
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0,0,0,0.92);
      display: flex; align-items: center; justify-content: center;
      cursor: zoom-in; touch-action: none;
    `

    const img = document.createElement('img')
    img.src = src
    img.alt = alt
    img.style.cssText = `
      max-width: 95vw; max-height: 90vh;
      object-fit: contain; border-radius: 6px;
      transform-origin: center center;
      transition: transform 0.1s ease;
      user-select: none; pointer-events: none;
    `

    const closeBtn = document.createElement('button')
    closeBtn.innerHTML = '&times;'
    closeBtn.style.cssText = `
      position: fixed; top: 16px; right: 20px;
      background: none; border: none; color: white;
      font-size: 2.5rem; line-height: 1; cursor: pointer;
      z-index: 10000; padding: 4px 8px;
    `

    overlay.appendChild(img)
    overlay.appendChild(closeBtn)
    document.body.appendChild(overlay)
    document.body.style.overflow = 'hidden'

    this._overlay = overlay
    this._img = img

    const close = () => this.close()
    closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close() })
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
    this._keyHandler = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', this._keyHandler)

    // Desktop: scroll to zoom, drag to pan
    overlay.addEventListener('wheel', (e) => this._onWheel(e), { passive: false })
    overlay.addEventListener('mousedown', (e) => this._onMouseDown(e))
    overlay.addEventListener('mousemove', (e) => this._onMouseMove(e))
    overlay.addEventListener('mouseup', () => this._onMouseUp())

    // Mobile: pinch to zoom, drag to pan
    overlay.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false })
    overlay.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false })
    overlay.addEventListener('touchend', (e) => this._onTouchEnd(e))
  }

  close() {
    if (this._overlay) {
      document.removeEventListener('keydown', this._keyHandler)
      this._overlay.remove()
      this._overlay = null
      document.body.style.overflow = ''
    }
  }

  _applyTransform() {
    this._img.style.transform = `translate(${this.originX}px, ${this.originY}px) scale(${this.scale})`
    this._overlay.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in'
  }

  _onWheel(e) {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.85 : 1.15
    this.scale = Math.min(Math.max(this.scale * delta, 1), 5)
    if (this.scale === 1) { this.originX = 0; this.originY = 0 }
    this._applyTransform()
  }

  _onMouseDown(e) {
    if (this.scale <= 1) return
    this.isDragging = true
    this.lastX = e.clientX
    this.lastY = e.clientY
    this._overlay.style.cursor = 'grabbing'
  }

  _onMouseMove(e) {
    if (!this.isDragging) return
    this.originX += e.clientX - this.lastX
    this.originY += e.clientY - this.lastY
    this.lastX = e.clientX
    this.lastY = e.clientY
    this._img.style.transition = 'none'
    this._applyTransform()
  }

  _onMouseUp() {
    this.isDragging = false
    if (this.scale > 1) this._overlay.style.cursor = 'grab'
  }

  _onTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      this.pinchStartDist = this._pinchDist(e.touches)
      this.pinchStartScale = this.scale
    } else if (e.touches.length === 1 && this.scale > 1) {
      this.isDragging = true
      this.lastX = e.touches[0].clientX
      this.lastY = e.touches[0].clientY
    }
  }

  _onTouchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      const dist = this._pinchDist(e.touches)
      this.scale = Math.min(Math.max(this.pinchStartScale * (dist / this.pinchStartDist), 1), 5)
      if (this.scale === 1) { this.originX = 0; this.originY = 0 }
      this._img.style.transition = 'none'
      this._applyTransform()
    } else if (e.touches.length === 1 && this.isDragging) {
      e.preventDefault()
      this.originX += e.touches[0].clientX - this.lastX
      this.originY += e.touches[0].clientY - this.lastY
      this.lastX = e.touches[0].clientX
      this.lastY = e.touches[0].clientY
      this._img.style.transition = 'none'
      this._applyTransform()
    }
  }

  _onTouchEnd(e) {
    if (e.touches.length < 2) this.isDragging = false
  }

  _pinchDist(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }
}
